import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { BidKind } from '@ebalance/proto/bfftypes/v1/bid_kind_pb'
import type { BidSegment } from '@ebalance/proto/bfftypes/v1/bid_segment_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import type {
  BidLimitCell,
  MarketOrderPolicyEntry,
} from '@ebalance/proto/bffuser/v1/bid_limit_model_pb'

import type { AutoBidPriceRange } from '~/pages/AdminApprovalFlow/models/autoBid'

/**
 * bid-limits の実効レンジを (segment, area, side) 単位で取り出す。
 * segment は業務処理で決まる (スポット = SPOT_NORMAL、 時間前 = INTRADAY。
 * bid-limits §2)。既存 bid-limits 読取 (AC-04: 「価格の上下限・
 * 成行可否・数量上限は 設定 ＞ 入札 が優先される」) の表示専用ヘルパー。
 * 未決定 / unlimited セルは undefined (= 制限なし) として扱う。
 */
export function autoBidPriceRangeFromCells(
  cells: readonly BidLimitCell[],
  areaCode: AreaCode,
  side: Side,
  segment: BidSegment,
): AutoBidPriceRange {
  const relevant = cells.filter(
    (c) =>
      c.bidSegment === segment && c.areaCode === areaCode && c.side === side,
  )
  const min = relevant.find((c) => c.kind === BidKind.MIN)
  const max = relevant.find((c) => c.kind === BidKind.MAX)
  return {
    min: min?.value !== undefined ? Number(min.value.value) : undefined,
    max: max?.value !== undefined ? Number(max.value.value) : undefined,
  }
}

/**
 * bid-limits の数量上限 (BidKind.QTY) を (segment, area, side) 単位で取り出す。
 * 未決定 / unlimited セルは undefined (= 制限なし) として扱う (AC-043: 数量上限も設定＞入札が優先される旨を表示)。
 */
export function autoBidQuantityLimitFromCells(
  cells: readonly BidLimitCell[],
  areaCode: AreaCode,
  side: Side,
  segment: BidSegment,
): number | undefined {
  const cell = cells.find(
    (c) =>
      c.bidSegment === segment &&
      c.areaCode === areaCode &&
      c.side === side &&
      c.kind === BidKind.QTY,
  )
  return cell?.value !== undefined ? Number(cell.value.value) : undefined
}

/**
 * 成行許可可否を (segment, area) 単位で取り出す。
 * MarketOrderPolicyEntry は (segment, area) 単位の toggle で買/売共通に
 * 適用される (bid-limits domain に side 概念が無い)。エントリが無い場合は
 * 既定=不許可 (spec §4 主要意思決定「成行の自動発注」: 初期値=不可)。
 */
export function autoBidMarketOrderAllowed(
  entries: readonly MarketOrderPolicyEntry[],
  areaCode: AreaCode,
  segment: BidSegment,
): boolean {
  const entry = entries.find(
    (e) => e.bidSegment === segment && e.areaCode === areaCode,
  )
  return entry?.allowed ?? false
}
